"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DotnetCoreAcquisitionWorker = void 0;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const fs = require("fs");
const os = require("os");
const path = require("path");
const rimraf = require("rimraf");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const versionUtils = require("./VersionUtilities");
const util_1 = require("util");
const TelemetryUtilities_1 = require("../EventStream/TelemetryUtilities");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const FileUtilities_1 = require("../Utils/FileUtilities");
const InstallIdUtilities_1 = require("../Utils/InstallIdUtilities");
const TypescriptUtilities_1 = require("../Utils/TypescriptUtilities");
const CacheTimeConstants_1 = require("./CacheTimeConstants");
const DirectoryProviderFactory_1 = require("./DirectoryProviderFactory");
const DotnetConditionValidator_1 = require("./DotnetConditionValidator");
const DotnetInstall_1 = require("./DotnetInstall");
const InstallTrackerSingleton_1 = require("./InstallTrackerSingleton");
const LinuxGlobalInstaller_1 = require("./LinuxGlobalInstaller");
const StringConstants_1 = require("./StringConstants");
const WinMacGlobalInstaller_1 = require("./WinMacGlobalInstaller");
class DotnetCoreAcquisitionWorker {
    constructor(utilityContext, extensionContext) {
        this.utilityContext = utilityContext;
        // @member usingNoInstallInvoker - Only use this for test when using the No Install Invoker to fake the worker into thinking a path is on disk.
        this.usingNoInstallInvoker = false;
        const dotnetExtension = os.platform() === 'win32' ? '.exe' : '';
        this.dotnetExecutable = `dotnet${dotnetExtension}`;
        this.globalResolver = null;
        this.extensionContext = extensionContext;
        this.file = new FileUtilities_1.FileUtilities();
    }
    uninstallAll(eventStream, storagePath, extensionState) {
        return __awaiter(this, void 0, void 0, function* () {
            eventStream.post(new EventStreamEvents_1.DotnetUninstallAllStarted());
            yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(eventStream, extensionState).uninstallAllRecords((0, DirectoryProviderFactory_1.directoryProviderFactory)('runtime', storagePath), () => __awaiter(this, void 0, void 0, function* () { yield this.deleteUninstalls(eventStream, storagePath); })); // runtime mode is ignored here
            yield this.ClearLegacyData(extensionState).catch(() => { });
            eventStream.post(new EventStreamEvents_1.DotnetUninstallAllCompleted());
        });
    }
    /**
     *
     * @remarks this is simply a wrapper around the acquire function.
     * @returns the requested dotnet path.
     */
    acquireLocalSDK(context, invoker) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.acquire(context, 'sdk', undefined, invoker);
        });
    }
    acquireGlobalSDK(context, installerResolver) {
        return __awaiter(this, void 0, void 0, function* () {
            this.globalResolver = installerResolver;
            return this.acquire(context, 'sdk', installerResolver);
        });
    }
    acquireLocalASPNET(context, invoker) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.acquire(context, 'aspnetcore', undefined, invoker);
        });
    }
    /**
     *
     * @remarks this is simply a wrapper around the acquire function.
     * @returns the requested dotnet path.
     */
    acquireLocalRuntime(context, invoker) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.acquire(context, 'runtime', undefined, invoker);
        });
    }
    /**
     * A function that allows installations to work in offline mode by preventing us from pinging the server,
     * to check if the .NET is the newest installed version.
     *
     * @param context
     * @returns null if no existing install matches with the same major.minor.
     * Else, returns the newest existing install that matches the major.minor.
     */
    getSimilarExistingInstall(context) {
        return __awaiter(this, void 0, void 0, function* () {
            const possibleInstallWithSameMajorMinor = (0, InstallIdUtilities_1.getInstallFromContext)(context);
            const installedVersions = yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).getExistingInstalls(context.installDirectoryProvider);
            for (const install of installedVersions) {
                if (install.dotnetInstall.installMode === possibleInstallWithSameMajorMinor.installMode &&
                    install.dotnetInstall.architecture === possibleInstallWithSameMajorMinor.architecture &&
                    install.dotnetInstall.isGlobal === possibleInstallWithSameMajorMinor.isGlobal &&
                    versionUtils.getMajorMinor(install.dotnetInstall.version, context.eventStream, context) ===
                        versionUtils.getMajorMinor(possibleInstallWithSameMajorMinor.version, context.eventStream, context)) {
                    // Requested version has already been installed.
                    const dotnetExePath = install.dotnetInstall.isGlobal ?
                        os.platform() === 'linux' ?
                            yield new LinuxGlobalInstaller_1.LinuxGlobalInstaller(context, this.utilityContext, install.dotnetInstall.version).getExpectedGlobalSDKPath(install.dotnetInstall.version, install.dotnetInstall.architecture) :
                            yield new WinMacGlobalInstaller_1.WinMacGlobalInstaller(context, this.utilityContext, install.dotnetInstall.version, '', '').getExpectedGlobalSDKPath(install.dotnetInstall.version, install.dotnetInstall.architecture) :
                        path.join(context.installDirectoryProvider.getInstallDir(install.dotnetInstall.installId), this.dotnetExecutable);
                    if (((yield this.file.exists(dotnetExePath)) || this.usingNoInstallInvoker)) {
                        context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionStatusResolved(possibleInstallWithSameMajorMinor, possibleInstallWithSameMajorMinor.version));
                        yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).trackInstalledVersion(context, install.dotnetInstall, dotnetExePath);
                        return { dotnetPath: dotnetExePath };
                    }
                }
            }
            return null;
        });
    }
    /**
     *
     * @param version The version of the runtime or sdk to check
     * @param installRuntime Whether this is a local runtime status check or a local SDK status check.
     * @param architecture The architecture of the install. Undefined means it will be the default arch, which is the node platform arch.
     * @returns The result of the install with the path to dotnet if installed, else undefined.
     */
    acquireStatus(context, installMode, architecture) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const version = context.acquisitionContext.version;
            const install = (0, DotnetInstall_1.GetDotnetInstallInfo)(version, installMode, 'local', architecture ? architecture : (_a = context.acquisitionContext.architecture) !== null && _a !== void 0 ? _a : this.getDefaultInternalArchitecture(context.acquisitionContext.architecture));
            const dotnetInstallDir = context.installDirectoryProvider.getInstallDir(install.installId);
            const dotnetPath = path.join(dotnetInstallDir, this.dotnetExecutable);
            const installedVersions = yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).getExistingInstalls(context.installDirectoryProvider);
            const existingInstall = yield this.getValidExistingInstallPath(context, installedVersions, install, dotnetPath);
            if (existingInstall) {
                context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionStatusResolved(install, install.version));
                return { dotnetPath: existingInstall };
            }
            // Version is not installed
            context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionStatusUndefined(install));
            return undefined;
        });
    }
    /**
     *
     * @param version the version to get of the runtime or sdk.
     * @param installRuntime true for runtime acquisition, false for SDK.
     * @param globalInstallerResolver Create this and add it to install globally.
     * @returns the dotnet acquisition result.
     */
    acquire(context_1, mode_1) {
        return __awaiter(this, arguments, void 0, function* (context, mode, globalInstallerResolver = null, localInvoker) {
            var _a;
            if (globalInstallerResolver) {
                context.acquisitionContext.version = yield globalInstallerResolver.getFullySpecifiedVersion();
            }
            const version = context.acquisitionContext.version;
            let install = (0, DotnetInstall_1.GetDotnetInstallInfo)(version, mode, globalInstallerResolver ? 'global' : 'local', (_a = context.acquisitionContext.architecture) !== null && _a !== void 0 ? _a : this.getDefaultInternalArchitecture(context.acquisitionContext.architecture));
            // Allow for the architecture to be null, which is a legacy behavior.
            if (context.acquisitionContext.architecture === null && context.acquisitionContext.architecture !== undefined) {
                install =
                    {
                        installId: (0, InstallIdUtilities_1.getInstallIdCustomArchitecture)(version, context.acquisitionContext.architecture, context.acquisitionContext.mode, globalInstallerResolver ? 'global' : 'local'),
                        version: install.version,
                        isGlobal: install.isGlobal,
                        installMode: mode,
                    };
            }
            context.eventStream.post(new EventStreamEvents_1.DotnetInstallIdCreatedEvent(`The requested version ${version} is now marked under the install: ${JSON.stringify(install)}.`));
            let acquisitionPromise = null;
            if (globalInstallerResolver) {
                acquisitionPromise = this.acquireGlobalCore(context, globalInstallerResolver, install).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    yield new CommandExecutor_1.CommandExecutor(context, this.utilityContext).endSudoProcessMaster(context.eventStream).catch(() => { });
                    const err = this.getErrorOrStringAsEventError(error);
                    throw err;
                }));
            }
            else {
                acquisitionPromise = this.acquireLocalCore(context, mode, install, localInvoker).catch((error) => {
                    const err = this.getErrorOrStringAsEventError(error);
                    throw err;
                });
            }
            return acquisitionPromise.then((res) => ({ dotnetPath: res }));
        });
    }
    /**
     *
     * @param version The version of the object to acquire.
     * @param installRuntime true if the request is to install the runtime, false for the SDK.
     * @param install The install record / id of the version managed by us.
     * @returns the dotnet path of the acquired dotnet.
     *
     * @remarks it is called "core" because it is the meat of the actual acquisition work; this has nothing to do with .NET core vs framework.
     */
    acquireLocalCore(context, mode, install, acquisitionInvoker) {
        return __awaiter(this, void 0, void 0, function* () {
            const version = context.acquisitionContext.version;
            return (0, TypescriptUtilities_1.executeWithLock)(context.eventStream, false, install.installId, CacheTimeConstants_1.LOCAL_LOCK_PING_DURATION_MS, context.timeoutSeconds * 1000, () => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                var _c, _d;
                const installedVersions = yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).getExistingInstalls(context.installDirectoryProvider);
                const dotnetInstallDir = context.installDirectoryProvider.getInstallDir(install.installId);
                const dotnetPath = path.join(dotnetInstallDir, this.dotnetExecutable);
                (_a = (_c = context.acquisitionContext).installType) !== null && _a !== void 0 ? _a : (_c.installType = 'local'); // Before this API param existed, all calls were for local types.
                (_b = (_d = context.acquisitionContext).architecture) !== null && _b !== void 0 ? _b : (_d.architecture = this.getDefaultInternalArchitecture(context.acquisitionContext.architecture));
                const existingInstall = yield this.getValidExistingInstallPath(context, installedVersions, install, dotnetPath);
                if (existingInstall !== null) {
                    return existingInstall;
                }
                context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionStarted(install, version, context.acquisitionContext.requestingExtensionId));
                yield acquisitionInvoker.installDotnet(install).catch((reason) => {
                    throw reason; // This will get handled and cast into an event based error by its caller.
                });
                context.installationValidator.validateDotnetInstall(install, dotnetPath);
                yield this.removeMatchingLegacyInstall(context, installedVersions, version, true);
                yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).trackInstalledVersion(context, install, dotnetPath);
                return dotnetPath;
            }));
        });
    }
    getValidExistingInstallPath(context, installedVersions, install, dotnetPath) {
        return __awaiter(this, void 0, void 0, function* () {
            const installExists = (yield this.file.exists(dotnetPath)) || this.usingNoInstallInvoker;
            const installIsInInstalledVersionsList = installedVersions.some(x => (0, DotnetInstall_1.IsEquivalentInstallation)(x.dotnetInstall, install));
            if (installIsInInstalledVersionsList && installExists) {
                try {
                    if (!this.usingNoInstallInvoker) {
                        context.installationValidator.validateDotnetInstall(install, dotnetPath, false, true);
                        const meetsRequirement = yield new DotnetConditionValidator_1.DotnetConditionValidator(context, this.utilityContext).dotnetMeetsRequirement(dotnetPath, { acquireContext: context.acquisitionContext, versionSpecRequirement: 'equal' });
                        if (!meetsRequirement) {
                            return null;
                        }
                    }
                }
                catch (error) {
                    context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionThoughtInstalledButNot(`Local Install ${JSON.stringify(install)} at ${dotnetPath} was tracked under installed but it wasn't found. Maybe it got removed externally.`));
                    yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).untrackInstalledVersion(context, install, true);
                    yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).reportSuccessfulUninstall(context, install, true);
                    return null;
                }
                if (context.acquisitionContext.installType === 'global') {
                    if (!(yield this.sdkIsFound(context, context.acquisitionContext.version))) {
                        context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionThoughtInstalledButNot(`Global Install ${JSON.stringify(install)} at ${dotnetPath} was tracked under installed but it wasn't found. Maybe it got removed externally.`));
                        yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).untrackInstalledVersion(context, install, true);
                        yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).reportSuccessfulUninstall(context, install, true);
                        return null;
                    }
                }
                context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionAlreadyInstalled(install, (context.acquisitionContext && context.acquisitionContext.requestingExtensionId)
                    ? context.acquisitionContext.requestingExtensionId : null));
                yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).trackInstalledVersion(context, install, dotnetPath);
                return dotnetPath;
            }
            return null;
        });
    }
    sdkIsFound(context, version) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const executor = new CommandExecutor_1.CommandExecutor(context, this.utilityContext);
            const listSDKsCommand = CommandExecutor_1.CommandExecutor.makeCommand('dotnet', ['--list-sdks', '--arch']);
            const result = yield executor.execute(listSDKsCommand, { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false);
            if (result.status !== '0') {
                return false;
            }
            if (os.platform() === 'linux' && ((_a = context === null || context === void 0 ? void 0 : context.acquisitionContext) === null || _a === void 0 ? void 0 : _a.mode) === 'sdk' && ((_b = context.acquisitionContext) === null || _b === void 0 ? void 0 : _b.installType) === 'global') {
                // There is a bug where the version marked in the folder / install is not latest if ubuntu is out of date for global installs
                return result.stdout.includes(versionUtils.getMajorMinor(version, context.eventStream, context));
            }
            return result.stdout.includes(version);
        });
    }
    getDefaultInternalArchitecture(existingArch) {
        if (existingArch !== null && existingArch !== undefined) {
            return existingArch;
        }
        if (existingArch === null) {
            return 'null';
        }
        return DotnetCoreAcquisitionWorker.defaultArchitecture();
    }
    static defaultArchitecture() {
        return os.arch();
    }
    getErrorOrStringAsEventError(error) {
        var _a, _b, _c, _d, _e;
        if (error instanceof EventStreamEvents_1.EventBasedError || error instanceof EventStreamEvents_1.EventCancellationError) {
            return error;
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (((_a = error === null || error === void 0 ? void 0 : error.error) === null || _a === void 0 ? void 0 : _a.message) !== undefined) // DotnetAcquisitionError is a bad but common pattern where the error is included in the thrown object
             {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                return new EventStreamEvents_1.EventBasedError((_c = (_b = error === null || error === void 0 ? void 0 : error.constructor) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : 'DotnetAcquisitionError', (_d = error === null || error === void 0 ? void 0 : error.error) === null || _d === void 0 ? void 0 : _d.message, error === null || error === void 0 ? void 0 : error.stack);
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            return new EventStreamEvents_1.EventBasedError('DotnetAcquisitionError', `.NET Acquisition Failed: ${(_e = error === null || error === void 0 ? void 0 : error.message) !== null && _e !== void 0 ? _e : JSON.stringify(error)}`);
        }
    }
    acquireGlobalCore(context, globalInstallerResolver, install) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (yield (0, TypescriptUtilities_1.isRunningUnderWSL)(context, this.utilityContext)) {
                const err = new EventStreamEvents_1.DotnetWSLSecurityError(new EventStreamEvents_1.EventCancellationError('DotnetWSLSecurityError', `Automatic .NET SDK Installation is not yet supported in WSL due to VS Code & WSL limitations.
            Please install the .NET SDK manually by following https://learn.microsoft.com/en-us/dotnet/core/install/linux-ubuntu. Then, add it to the path by following https://github.com/dotnet/vscode-dotnet-runtime/blob/main/Documentation/troubleshooting-runtime.md#manually-installing-net`), (0, InstallIdUtilities_1.getInstallFromContext)(context));
                context.eventStream.post(err);
                throw err.error;
            }
            const installingVersion = yield globalInstallerResolver.getFullySpecifiedVersion();
            context.eventStream.post(new EventStreamEvents_1.DotnetGlobalVersionResolutionCompletionEvent(`The version we resolved that was requested is: ${installingVersion}.`));
            const installedVersions = yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).getExistingInstalls(context.installDirectoryProvider);
            const installer = os.platform() === 'linux' ?
                new LinuxGlobalInstaller_1.LinuxGlobalInstaller(context, this.utilityContext, installingVersion) :
                new WinMacGlobalInstaller_1.WinMacGlobalInstaller(context, this.utilityContext, installingVersion, yield globalInstallerResolver.getInstallerUrl(), yield globalInstallerResolver.getInstallerHash());
            // See if we should return a fake path instead of running the install
            if (process.env.VSCODE_DOTNET_GLOBAL_INSTALL_FAKE_PATH && process.env.VSCODE_DOTNET_GLOBAL_INSTALL_FAKE_PATH === 'true') {
                context.eventStream.post(new EventStreamEvents_1.DotnetFakeSDKEnvironmentVariableTriggered(`VSCODE_DOTNET_GLOBAL_INSTALL_FAKE_PATH has been set.`));
                return 'fake-sdk';
            }
            let dotnetExePath = yield installer.getExpectedGlobalSDKPath(installingVersion, (_a = context.acquisitionContext.architecture) !== null && _a !== void 0 ? _a : this.getDefaultInternalArchitecture(context.acquisitionContext.architecture), false);
            const existingInstall = yield this.getValidExistingInstallPath(context, installedVersions, install, dotnetExePath);
            if (existingInstall) {
                return existingInstall;
            }
            context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionStarted(install, installingVersion, context.acquisitionContext.requestingExtensionId));
            context.eventStream.post(new EventStreamEvents_1.DotnetBeginGlobalInstallerExecution(`Beginning to run installer for ${JSON.stringify(install)} in ${os.platform()}.`));
            const installerResult = yield installer.installSDK(install);
            context.eventStream.post(new EventStreamEvents_1.DotnetCompletedGlobalInstallerExecution(`Completed installer for ${JSON.stringify(install)} in ${os.platform()}.`));
            if (installerResult !== '0') {
                // For user-friendly exit codes, show only the interpreted message without verbose details
                const interpretedMessage = WinMacGlobalInstaller_1.WinMacGlobalInstaller.InterpretExitCode(installerResult);
                const errorMessage = WinMacGlobalInstaller_1.WinMacGlobalInstaller.IsUserFriendlyExitCode(installerResult)
                    ? interpretedMessage
                    : `An error was raised by the .NET SDK installer. The exit code it gave us: ${installerResult}.
${interpretedMessage}`;
                const err = new EventStreamEvents_1.DotnetNonZeroInstallerExitCodeError(new EventStreamEvents_1.EventBasedError('DotnetNonZeroInstallerExitCodeError', errorMessage), install);
                context.eventStream.post(err);
                throw err;
            }
            TelemetryUtilities_1.TelemetryUtilities.setDotnetSDKTelemetryToMatch(context.isExtensionTelemetryInitiallyEnabled, this.extensionContext, context, this.utilityContext).catch(() => { });
            // in case the path does not exist, try resetting the path using an automatic path search setting
            dotnetExePath = yield installer.getExpectedGlobalSDKPath(installingVersion, (_b = context.acquisitionContext.architecture) !== null && _b !== void 0 ? _b : this.getDefaultInternalArchitecture(context.acquisitionContext.architecture));
            context.installationValidator.validateDotnetInstall(install, dotnetExePath, os.platform() === 'darwin', os.platform() !== 'darwin');
            context.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionCompleted(install, dotnetExePath, installingVersion));
            yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).trackInstalledVersion(context, install, dotnetExePath);
            yield new CommandExecutor_1.CommandExecutor(context, this.utilityContext).endSudoProcessMaster(context.eventStream);
            context.eventStream.post(new EventStreamEvents_1.DotnetGlobalAcquisitionCompletionEvent(`The version ${JSON.stringify(install)} completed successfully.`));
            return dotnetExePath;
        });
    }
    /**
     *
     * @param installedVersions - all of the currently installed versions of dotnet managed by the extension
     * @param version - the version that is about to be installed
     *
     * @remarks Before, installed versions used their version as the 'install id' in the promises and folder structure.
     * We changed this install id to include architecture so different architectures could be installed side-by-side.
     * This means any installs that were made before version 1.8.0 will not have the architecture in their install id.
     * They should be removed. This is what makes an install 'legacy'.
     *
     * This function only removes the legacy install with the same version as 'version'.
     * That's because removing other legacy installs may cause a breaking change.
     * Assuming the install succeeds, this will not break as the legacy install of 'version' will be replaced by a non-legacy one upon completion.
     *
     * Many (if not most) legacy installs will actually hold the same content as the newly installed runtime/sdk.
     * But since we don't want to be in the business of detecting their architecture, we chose this option as opposed to renaming and install id and folder
     * ... for the legacy install.
     *
     * Note : only local installs were ever 'legacy.'
     */
    removeMatchingLegacyInstall(context_1, installedVersions_1, version_1) {
        return __awaiter(this, arguments, void 0, function* (context, installedVersions, version, alreadyHoldingLock = false) {
            const legacyInstalls = this.existingLegacyInstalls(context, installedVersions);
            for (const legacyInstall of legacyInstalls) {
                if (legacyInstall.dotnetInstall.installId.includes(version)) {
                    context.eventStream.post(new EventStreamEvents_1.DotnetLegacyInstallRemovalRequestEvent(`Trying to remove legacy install: ${JSON.stringify(legacyInstall)} of ${version}.`));
                    yield this.uninstallLocal(context, legacyInstall.dotnetInstall, false, alreadyHoldingLock);
                }
            }
        });
    }
    /**
     *
     * @param allInstalls all of the existing installs.
     * @returns All existing installs made by the extension that don't include a - for the architecture. Not all of the ones which use a string type.
     */
    existingLegacyInstalls(context, allInstalls) {
        let legacyInstalls = [];
        for (const install of allInstalls) {
            // Assumption: .NET versions so far did not include ~ in them, but we do for our non-legacy ids.
            if (!install.dotnetInstall.installId.includes('~')) {
                context.eventStream.post(new EventStreamEvents_1.DotnetLegacyInstallDetectedEvent(`A legacy install was detected -- ${JSON.stringify(install)}.`));
                legacyInstalls = legacyInstalls.concat(install);
            }
        }
        return legacyInstalls;
    }
    ClearLegacyData(extensionState) {
        return __awaiter(this, void 0, void 0, function* () {
            yield extensionState.update('installPathsGraveyard', '');
        });
    }
    uninstallLocal(context_1, install_1) {
        return __awaiter(this, arguments, void 0, function* (context, install, force = false, alreadyHoldingLock = false, useLiveDependentCheck = false) {
            this.ClearLegacyData(context.extensionState).catch(() => { });
            return (0, TypescriptUtilities_1.executeWithLock)(context.eventStream, alreadyHoldingLock, install.installId, CacheTimeConstants_1.LOCAL_LOCK_PING_DURATION_MS, context.timeoutSeconds * 1000, () => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                if (install.isGlobal) {
                    return '0';
                }
                const dotnetInstallDir = context.installDirectoryProvider.getInstallDir(install.installId);
                const dotnetExecutablePath = path.join(dotnetInstallDir, this.dotnetExecutable);
                try {
                    yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).untrackInstalledVersion(context, install, force);
                    const noRelevantDependents = force ? true : useLiveDependentCheck ?
                        yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).installHasNoLiveDependentsBesidesId(dotnetExecutablePath, context.installDirectoryProvider, (_a = context.acquisitionContext.requestingExtensionId) !== null && _a !== void 0 ? _a : '', install)
                        : yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).installHasNoRegisteredDependentsBesidesId(install, context.installDirectoryProvider, false, (_b = context.acquisitionContext.requestingExtensionId) !== null && _b !== void 0 ? _b : '');
                    if (force || noRelevantDependents) {
                        context.eventStream.post(new EventStreamEvents_1.DotnetUninstallStarted(`Attempting to remove .NET ${install.installId}.`));
                        yield this.file.wipeDirectory(dotnetInstallDir, context.eventStream, undefined, true);
                        yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).reportSuccessfulUninstall(context, install, force);
                        context.eventStream.post(new EventStreamEvents_1.DotnetUninstallCompleted(`Uninstalled .NET ${install.installId}.`));
                    }
                    else {
                        context.eventStream.post(new EventStreamEvents_1.DotnetUninstallSkipped(`Removed reference of ${JSON.stringify(install)} in path ${dotnetInstallDir}, but did not uninstall.
Other dependents remain.`));
                    }
                    return '0';
                }
                catch (error) {
                    context.eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `The attempt to uninstall .NET ${install.installId} failed - was .NET in use?`));
                    yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).trackInstalledVersion(context, install, dotnetExecutablePath);
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    return (_c = error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : '1';
                }
            }));
        });
    }
    uninstallGlobal(context_1, install_1, globalInstallerResolver_1) {
        return __awaiter(this, arguments, void 0, function* (context, install, globalInstallerResolver, force = false) {
            return (0, TypescriptUtilities_1.executeWithLock)(context.eventStream, false, (0, StringConstants_1.GLOBAL_INSTALL_STATE_MODIFIER_LOCK)(context.installDirectoryProvider, install), CacheTimeConstants_1.GLOBAL_LOCK_PING_DURATION_MS, context.timeoutSeconds * 1000, () => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                let systemInstallPath = '';
                try {
                    context.eventStream.post(new EventStreamEvents_1.DotnetUninstallStarted(`Attempting to remove .NET ${install.installId}.`));
                    yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).untrackInstalledVersion(context, install, force);
                    // Note: it's ok not to check live dependents here (though we could) since this will require UAC and extensions do not depend on us to auto-manage admin installs
                    if (force || (yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).installHasNoRegisteredDependentsBesidesId(install, context.installDirectoryProvider, false, (_a = context.acquisitionContext.requestingExtensionId) !== null && _a !== void 0 ? _a : ''))) {
                        const installingVersion = yield globalInstallerResolver.getFullySpecifiedVersion();
                        const installer = os.platform() === 'linux' ?
                            new LinuxGlobalInstaller_1.LinuxGlobalInstaller(context, this.utilityContext, installingVersion) :
                            new WinMacGlobalInstaller_1.WinMacGlobalInstaller(context, this.utilityContext, installingVersion, yield globalInstallerResolver.getInstallerUrl(), yield globalInstallerResolver.getInstallerHash());
                        systemInstallPath = yield installer.getExpectedGlobalSDKPath(installingVersion, install.architecture);
                        const ok = yield installer.uninstallSDK(install);
                        yield new CommandExecutor_1.CommandExecutor(context, this.utilityContext).endSudoProcessMaster(context.eventStream);
                        if (ok === '0') {
                            yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).reportSuccessfulUninstall(context, install, force);
                            context.eventStream.post(new EventStreamEvents_1.DotnetUninstallCompleted(`Uninstalled .NET ${install.installId}.`));
                            return '0';
                        }
                    }
                    context.eventStream.post(new EventStreamEvents_1.DotnetUninstallFailed(`Failed to uninstall .NET ${install.installId}. Another install may be in progress? Uninstall manually or delete the folder.`));
                    return '117778'; // arbitrary error code to indicate uninstall failed without error.
                }
                catch (error) {
                    yield new CommandExecutor_1.CommandExecutor(context, this.utilityContext).endSudoProcessMaster(context.eventStream);
                    context.eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `The attempt to uninstall .NET ${install.installId} failed - was .NET in use?`));
                    yield InstallTrackerSingleton_1.InstallTrackerSingleton.getInstance(context.eventStream, context.extensionState).trackInstalledVersion(context, install, systemInstallPath);
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    return (_b = error === null || error === void 0 ? void 0 : error.message) !== null && _b !== void 0 ? _b : '1';
                }
            }));
        });
    }
    deleteUninstalls(eventStream, folderPath) {
        return __awaiter(this, void 0, void 0, function* () {
            eventStream.post(new EventStreamEvents_1.DotnetAcquisitionDeletion(folderPath));
            try {
                yield fs.promises.chmod(folderPath, 0o744);
            }
            catch (error) {
                eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to chmod +x on .NET folder ${folderPath} when marked for deletion.`));
            }
            try {
                const subDirectoryPaths = (yield fs.promises.readdir(folderPath, { withFileTypes: true })).filter(entry => entry.isDirectory()).map(entry => { return path.join(folderPath, entry.name); });
                for (const fullSubDirectoryPath of subDirectoryPaths) {
                    if (yield FileUtilities_1.FileUtilities.fileIsOpen(path.join(fullSubDirectoryPath, (0, TypescriptUtilities_1.getDotnetExecutable)()), eventStream)) {
                        eventStream.post(new EventStreamEvents_1.DotnetUninstallSkipped(`Not uninstalling .NET, as it's in use, at ${folderPath}.`));
                        continue;
                    }
                    try {
                        yield (0, util_1.promisify)(rimraf)(fullSubDirectoryPath);
                        eventStream.post(new EventStreamEvents_1.DotnetAcquisitionDeletion(`Deleted .NET folder ${folderPath} when marked for deletion.`));
                    }
                    catch (error) {
                        eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to delete .NET folder ${folderPath} when marked for deletion.`));
                    }
                }
            }
            catch (error) {
                eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `Failed to read directory ${folderPath}.`));
            }
        });
    }
}
exports.DotnetCoreAcquisitionWorker = DotnetCoreAcquisitionWorker;
//# sourceMappingURL=DotnetCoreAcquisitionWorker.js.map